import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { CurrentUser } from "sdk/currentUser";
import { useLazyCurrentUserIndexQuery } from "~/store/api";
import { signOut, setCurrentUser } from "~/store/authentication";
import { useAppDispatch } from "~/store/hooks";
import { setAmplitudeUser, resetAmplitudeUser } from "~/utils/amplitude/identity";

interface UseAuthN {
  handleSetUser: (user: CurrentUser) => void;
  handleUserSignOut: () => void;
  refreshCurrentUser: () => Promise<void>;
}

export const useAuthN = (): UseAuthN => {
  const dispatch = useAppDispatch();
  const [apiCurrentUserIndex] = useLazyCurrentUserIndexQuery();

  const handleSetUser = useCallback(
    (user: CurrentUser) => {
      dispatch(setCurrentUser(user));

      setAmplitudeUser(user);

      Sentry.setUser(user);
    },
    [dispatch],
  );

  const handleUserSignOut = useCallback((): void => {
    // These are to ensure that Sentry and Front do not remember the user
    Sentry.setUser(null);
    resetAmplitudeUser();

    dispatch(signOut());
  }, [dispatch]);

  // This will always be needed as long as we need results from TypeForm. This
  // is because on submit, we call TypeForm to get the User's response, then
  // set certain things from that.
  const refreshCurrentUser = useCallback(async () => {
    try {
      const responseBody = await apiCurrentUserIndex().unwrap();
      const currentUser = responseBody.data.attributes;
      handleSetUser(currentUser);
    } catch (e: any) {
      Sentry.captureException(e);
    }
  }, [apiCurrentUserIndex, handleSetUser]);

  return {
    handleSetUser,
    handleUserSignOut,
    refreshCurrentUser,
  };
};
